import { config } from "config";
import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import { NativeSettings, AndroidSettings, IOSSettings } from "capacitor-native-settings";

import { dataHelper } from "helper/data";
import { Capacitor } from "@capacitor/core";
import { registerPlugin } from "@capacitor/core";

const pushNotification = {};
let isRegistrationListenerAttached = false;
let isForegroundListenerAttached = false;
let latestPushToken = null;
const isNativePushRuntime = () => Capacitor.isNativePlatform();
const NotificationHelper = registerPlugin("NotificationHelper");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** APNs device token as 32-byte hex (Capacitor `registration` on iOS) — not valid as an FCM registration token. */
const looksLikeApnsDeviceTokenHex = (t) =>
  typeof t === "string" && /^[0-9a-f]{64}$/i.test(t.trim());

/** Client-side placeholder from `getToken` when push is unavailable — not an FCM/APNs token. */
pushNotification.isPlaceholderDeviceId = (id) =>
  id == null || String(id).startsWith("DEVICEID_");

pushNotification.openNotificationSettings = async () => {
  if (!config.isMobileApp || !isNativePushRuntime()) {
    return;
  }
  try {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.App,
    });
  } catch (e) {
    console.error("pushNotification.openNotificationSettings error", e);
  }
};

pushNotification.isPermissionGranted = async () => {
  if (!config.isMobileApp || !isNativePushRuntime()) {
    return false;
  }
  try {
    const permissions = await PushNotifications.checkPermissions();
    const hasRuntimePermission = permissions?.receive === "granted";
    if (!hasRuntimePermission) {
      return false;
    }

    // On Android, runtime permission can be granted while app-level notifications
    // are disabled in system settings. Use native bridge to read actual toggle state.
    if (Capacitor.getPlatform() === "android") {
      try {
        const status = await NotificationHelper.areNotificationsEnabled();
        return Boolean(status?.enabled);
      } catch (e) {
        // Fail safe on bridge issues: use runtime permission state.
        return hasRuntimePermission;
      }
    }

    return hasRuntimePermission;
  } catch (e) {
    return false;
  }
};

pushNotification.ensurePermission = async ({
  openSettingsIfDenied = false,
} = {}) => {
  if (!config.isMobileApp) {
    return true;
  }

  try {
    // Attach listeners before register() so the `registration` event is never missed.
    await pushNotification.received();

    const permissions = await PushNotifications.checkPermissions();
    let receivePermission = permissions?.receive;

    if (receivePermission === "prompt" || receivePermission === "prompt-with-rationale") {
      const requestResult = await PushNotifications.requestPermissions();
      receivePermission = requestResult?.receive;
      console.log("Permission after request:", receivePermission);
    }

    if (receivePermission === "granted") {
      await PushNotifications.register();
      return true;
    }

    if (receivePermission === "denied") {
      console.warn("Permission denied - must open settings");

      if (openSettingsIfDenied) {
        await pushNotification.openNotificationSettings();
      }
    }

    return false;
  } catch (e) {
    console.error("pushNotification.ensurePermission error", e);
    return false;
  }
};

pushNotification.init = async () => {
  if (!config.isMobileApp) {
    return false;
  }

  await pushNotification.received();
  await pushNotification.ensurePermission();
};

/**
 * After the user returns from system Settings (or toggles notifications), re-run
 * native registration so FCM/APNs tokens stay in sync with Redux / backend.
 */
pushNotification.refreshPushRegistrationAfterResume = async () => {
  if (!config.isMobileApp || !isNativePushRuntime()) {
    return null;
  }
  try {
    await pushNotification.received();

    const granted = await pushNotification.isPermissionGranted();
    if (!granted) {
      return null;
    }

    latestPushToken = null;
    await PushNotifications.register();

    const deadline = Date.now() + 3000;
    while (Date.now() < deadline) {
      if (latestPushToken) {
        return latestPushToken;
      }
      await sleep(100);
    }

    try {
      const { token } = await FCM.getToken();

      console.log("token", token);


      if (token) {
        latestPushToken = token;
        return token;
      }
    } catch (e) {
      console.error("refreshPushRegistrationAfterResume FCM.getToken", e);
    }

    return latestPushToken;
  } catch (e) {
    console.error("refreshPushRegistrationAfterResume error", e);
    return null;
  }
};

pushNotification.received = async () => {
  if (!isRegistrationListenerAttached) {
    await PushNotifications.addListener("registration", async ({ value }) => {
      let token = value; // APNS hex on iOS, FCM on Android
      const platform = Capacitor.getPlatform();
      if (platform === "ios") {
        try {
          const { token: fcmToken } = await FCM.getToken();
          if (fcmToken) {
            token = fcmToken;
          } else if (looksLikeApnsDeviceTokenHex(token)) {
            console.warn(
              "[push] iOS: FCM.getToken empty; APNs hex is not usable as an FCM target. Check Firebase / GoogleService-Info.plist.",
            );
          }
        } catch (e) {
          console.error("Push registration: FCM.getToken (iOS)", e);
        }
      }
      latestPushToken = token || null;
    });

    await PushNotifications.addListener("registrationError", (error) => {
      console.error("Push registration error: ", error);
    });
    isRegistrationListenerAttached = true;
  }

  if (!isForegroundListenerAttached) {
    await PushNotifications.addListener(
      "pushNotificationReceived",
      (notification) => {
        console.log("Push notification received: ", notification);
      },
    );

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification) => {
        console.log(
          "Push notification action performed",
          notification.actionId,
          notification.inputValue,
        );
      },
    );
    isForegroundListenerAttached = true;
  }
};

pushNotification.getToken = async (cb) => {
  // we want only in mobile
  // for now we will use only for android
  if (!config.isMobileApp || !isNativePushRuntime()) {
    cb(dataHelper.generateUniqueId());
    return;
  }

  if (!isRegistrationListenerAttached) {
    await pushNotification.received();
  }

  // On iOS, `registration` can fire with APNs hex before FCM resolves; never short-circuit with hex alone.
  if (
    latestPushToken &&
    !(Capacitor.getPlatform() === "ios" && looksLikeApnsDeviceTokenHex(latestPushToken))
  ) {
    cb(latestPushToken);
    return;
  }

  try {
    const hasPermission = await pushNotification.ensurePermission();
    if (!hasPermission) {
      cb(dataHelper.generateUniqueId());
      return;
    }

    // Give registration + FCM time; on iOS always prefer explicit FCM.getToken for the callback payload.
    setTimeout(async () => {
      try {
        if (Capacitor.getPlatform() === "ios") {
          const { token: fcmToken } = await FCM.getToken();
          if (fcmToken) {
            latestPushToken = fcmToken;
            cb(fcmToken);
            return;
          }
        } else if (latestPushToken) {
          cb(latestPushToken);
          return;
        }
        const { token } = await FCM.getToken();
        if (token) {
          latestPushToken = token;
          cb(token);
          return;
        }
      } catch (e) {
        console.error("pushNotification.getToken FCM.getToken", e);
      }
      if (latestPushToken) {
        cb(latestPushToken);
        return;
      }
      cb(dataHelper.generateUniqueId());
    }, 700);
  } catch (e) {
    console.error("pushNotification.getToken error", e);
    cb(dataHelper.generateUniqueId());
  }
};

export { pushNotification };
